import { memo, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { usePrevious } from 'react-use';

import { type Clip } from '@air/api/types';

import { type ClipDiscussion as ClipDiscussionType } from '~/components/Annotations/shared/types';
import { NEW_DISCUSSION_ID } from '~/components/AnnotationsV2/shared/useActiveDiscussionIdObserver';
import {
  useDiscussionPanelContext,
  useDiscussionPanelContextSelector,
} from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { DiscussionsLoadMoreItem } from '~/components/DiscussionsV2/components/DiscussionsLoadMoreItem';
import {
  DiscussionsVirtualizedList,
  type DiscussionsVirtualizedListHandle,
  type DiscussionsVirtualizedListProps,
} from '~/components/DiscussionsV2/components/DiscussionsVirtualizedList';
import { ClipDiscussion, type ClipDiscussionProps } from '~/components/DiscussionsV2/Discussion/ClipDiscussion';
import { DiscussionSkeleton } from '~/components/DiscussionsV2/Discussion/components/DiscussionSkeleton';
import { type CommentMenuProps } from '~/components/DiscussionsV2/Discussion/DiscussionComment/components/CommentMenu';
import { type DiscussionCommentProps } from '~/components/DiscussionsV2/Discussion/DiscussionComment/DiscussionComment';
import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { DISCUSSIONS_LIST } from '~/constants/testIDs';

const SCROLL_TO_DISCUSSION_DELAY_MS = 100;

export type DiscussionsListProps = {
  discussions: ClipDiscussionType[];
  isLoading: boolean;
  /**
   * @see https://github.com/signavio/react-mentions
   */
  mentionsList: MentionsData[];
  isLoadingMentions?: boolean;
  onCommentDelete: CommentMenuProps['onDelete'];
  onCommentEdit: DiscussionCommentProps['onSave'];
  onToggleCommentResolve?: CommentMenuProps['onToggleResolve'];
  onCommentSubmit?: (comment: string, discussion: ClipDiscussionType, clipId: Clip['id']) => void;
  onSwitchVersion?: ClipDiscussionProps['onSwitchVersion'];
  shouldShowMentionsButton?: boolean;
  onAnnotatedCommentClick: ClipDiscussionProps['onAnnotatedCommentClick'];
  canReply?: boolean;
  emptyState?: ReactNode;

  hasNextPage: boolean;
  fetchNextPage: () => void;
  totalDiscussions: number;
};

export const DiscussionsList = memo(
  ({
    discussions,
    canReply = true,
    isLoading,
    onCommentDelete,
    onCommentEdit,
    onToggleCommentResolve,
    onCommentSubmit,
    isLoadingMentions,
    mentionsList,
    onSwitchVersion,
    shouldShowMentionsButton,
    onAnnotatedCommentClick,
    emptyState,
    hasNextPage,
    fetchNextPage,
    totalDiscussions,
  }: DiscussionsListProps) => {
    const listRef = useRef<DiscussionsVirtualizedListHandle>(null);

    const previousDiscussionsTotal = usePrevious(totalDiscussions);

    const { clearScrollToDiscussionId } = useDiscussionPanelContext();

    // Get the replyingDiscussionId from context
    const replyingDiscussionId = useDiscussionPanelContextSelector((state) => state.replyingDiscussionId);

    // Get the scrollToDiscussionId from context (set by marker clicks)
    const scrollToDiscussionId = useDiscussionPanelContextSelector((state) => state.scrollToDiscussionId);

    // Helper function to scroll to a discussion by ID
    const scrollToDiscussion = useCallback(
      (discussionId: string) => {
        if (!discussionId || discussions.length === 0) {
          return false;
        }

        const discussionIndex = discussions.findIndex((d) => d.id === discussionId);

        if (discussionIndex !== -1) {
          // Discussion found - scroll to it
          // Convert to virtualized list index (reversed order)
          const virtualIndex = discussions.length - discussionIndex - 1;
          const finalIndex = hasNextPage ? virtualIndex + 1 : virtualIndex;

          // Small delay to ensure the list is rendered
          setTimeout(() => {
            // Always scroll to center the discussion in the viewport
            listRef.current?.scrollToIndex(finalIndex);
          }, SCROLL_TO_DISCUSSION_DELAY_MS);
          return true;
        }

        return false;
      },
      [discussions, hasNextPage],
    );

    useEffect(() => {
      if (previousDiscussionsTotal && totalDiscussions > previousDiscussionsTotal) {
        listRef.current?.scrollToBottom();
      }
    }, [previousDiscussionsTotal, totalDiscussions]);

    // Scroll to discussion when replying
    useEffect(() => {
      if (replyingDiscussionId) {
        scrollToDiscussion(replyingDiscussionId);
      }
    }, [replyingDiscussionId, scrollToDiscussion]);

    // Scroll to discussion when scrollToDiscussionId changes (e.g., from video timeline marker click)
    useEffect(() => {
      if (!scrollToDiscussionId || discussions.length === 0) {
        return;
      }

      const found = scrollToDiscussion(scrollToDiscussionId);

      if (found) {
        // Successfully scrolled - clear the scroll target
        clearScrollToDiscussionId();
      } else if (hasNextPage && !isLoading) {
        // Discussion not found and more pages available - fetch next page
        // This will keep fetching pages until the discussion is found or no more pages exist
        fetchNextPage();
      }
    }, [
      scrollToDiscussionId,
      discussions,
      scrollToDiscussion,
      hasNextPage,
      isLoading,
      fetchNextPage,
      clearScrollToDiscussionId,
    ]);

    const renderComments = useCallback<DiscussionsVirtualizedListProps['children']>(
      ({ index }) => {
        if (hasNextPage && index === 0) {
          return (
            <DiscussionsLoadMoreItem
              scrollElementRef={listRef.current?.getScrollElement()}
              isLoading={isLoading}
              loadMore={fetchNextPage}
            />
          );
        } else {
          const itemIndex = hasNextPage ? index : index + 1;
          const discussion = discussions[discussions.length - itemIndex];

          return (
            <ClipDiscussion
              discussion={discussion}
              clipId={discussion.clipId}
              /**
               * We use `NEW_DISCUSSION_ID` for temporary comments in cache. However, since we use
               * the `discussion.id` as the key for the discussion, we need to append the index to
               * the key to avoid React from reusing the same DOM element for different discussions.
               */
              key={discussion.id === NEW_DISCUSSION_ID ? `${discussion.id}-${index}` : discussion.id}
              isLoadingMentions={isLoadingMentions}
              mentionsList={mentionsList}
              canReply={canReply}
              onDelete={onCommentDelete}
              onEdit={onCommentEdit}
              onToggleResolve={onToggleCommentResolve}
              onSubmit={onCommentSubmit}
              onSwitchVersion={onSwitchVersion}
              shouldShowMentionsButton={shouldShowMentionsButton}
              onAnnotatedCommentClick={onAnnotatedCommentClick}
            />
          );
        }
      },
      [
        canReply,
        discussions,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isLoadingMentions,
        mentionsList,
        onAnnotatedCommentClick,
        onCommentDelete,
        onCommentEdit,
        onCommentSubmit,
        onSwitchVersion,
        onToggleCommentResolve,
        shouldShowMentionsButton,
      ],
    );

    const isInitialLoadingSkeleton = isLoading && !discussions.length;
    const hasDiscussions = !!discussions?.length;

    let content: ReactNode;

    if (isInitialLoadingSkeleton) {
      content = (
        <div className="flex size-full flex-col justify-start px-5" data-testid="DISCUSSIONS_LIST_LOADING">
          <DiscussionSkeleton />
          <DiscussionSkeleton />
        </div>
      );
    } else if (hasDiscussions) {
      content = (
        <DiscussionsVirtualizedList
          ref={listRef}
          itemsCount={hasNextPage ? discussions.length + 1 : discussions.length}
        >
          {renderComments}
        </DiscussionsVirtualizedList>
      );
    } else {
      content = emptyState;
    }

    return (
      <div className="flex h-full overflow-y-auto">
        <div className="relative flex max-h-full w-full flex-1" data-testid={DISCUSSIONS_LIST}>
          {content}
        </div>
      </div>
    );
  },
);

DiscussionsList.displayName = 'DiscussionsList';
